import type { ReactNode } from 'react'
import { NavLink, Outlet } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { useNotifications } from '@/hooks/useNotifications'
import { useClientReport } from '@/hooks/useClientReport'
import { useOnboarding } from '@/hooks/useOnboarding'
import { useStatus } from '@/hooks/useStatus'
import { Button } from './Button'
import { InstallNudge } from './InstallNudge'
import { Onboarding } from './Onboarding'
import { OutageBanner } from './OutageBanner'
import { PendingApprovalBanner } from './PendingApprovalBanner'

function IconHome() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden="true">
      <path
        d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-5H9v5H5a1 1 0 0 1-1-1z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function IconRequests() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden="true">
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 9.5h17M9 4.5v15" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}

function IconAccount() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden="true">
      <circle cx="12" cy="8.5" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M4.5 20a7.5 7.5 0 0 1 15 0"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

function IconAdmin() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden="true">
      <path
        d="M12 3.5 19 6v5.5c0 4-2.9 7.4-7 8.9-4.1-1.5-7-4.9-7-8.9V6z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  )
}

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  end?: boolean
}

/**
 * The wordmark doubles as the board's status lamp: green when the server is
 * reachable, amber when it is not. Putting the indicator in the masthead means
 * the outage state is legible from every screen without a banner having to
 * shout on each one.
 */
function Masthead() {
  const status = useStatus()
  const online = !status.isDegraded
  const known = !status.isLoading

  return (
    <NavLink to="/" className="flex items-baseline gap-2.5" aria-label="Hermes home">
      <span className="signage text-[15px] text-ink">Hermes</span>
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden
          className={`size-1.5 rounded-full ${
            !known ? 'bg-faint' : online ? 'bg-ok' : 'bg-warn'
          }`}
        />
        <span className="signage text-[9px] text-faint">
          {!known ? '····' : online ? 'Live' : 'No feed'}
        </span>
      </span>
    </NavLink>
  )
}

export function AppLayout() {
  const { user, isAdmin, logout } = useAuth()
  const unread = useNotifications({ unreadOnly: true, limit: 20 })
  // Lives here rather than on the dashboard so it survives navigation and is
  // reachable from every screen.
  const onboarding = useOnboarding()
  // Reported once the push state has settled, so it is not recorded as
  // "unsupported" while detection is still running.
  useClientReport(onboarding.push.status !== 'checking')
  const unreadCount = unread.data?.total ?? unread.data?.items.length ?? 0

  const items: NavItem[] = [
    { to: '/', label: 'Board', icon: <IconHome />, end: true },
    { to: '/requests', label: 'Requests', icon: <IconRequests /> },
    { to: '/account', label: 'Account', icon: <IconAccount /> },
  ]
  if (isAdmin) items.push({ to: '/admin', label: 'Admin', icon: <IconAdmin /> })

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-elevated focus:px-3 focus:py-2 focus:text-sm"
      >
        Skip to content
      </a>

      <div className="pt-safe sticky top-0 z-30 border-b border-line bg-canvas/95 backdrop-blur">
        <OutageBanner />
        <PendingApprovalBanner />
        <InstallNudge state={onboarding} />
        <header className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3">
          <Masthead />

          <nav aria-label="Main" className="ml-6 hidden items-center gap-1 sm:flex">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `signage rounded-[3px] px-3 py-2 text-[10px] transition ${
                    isActive
                      ? 'bg-accent-dim text-accent'
                      : 'text-muted hover:bg-elevated hover:text-ink'
                  }`
                }
              >
                {item.label}
                {item.to === '/' && unreadCount > 0 ? (
                  <span className="ml-1.5 font-mono text-accent">{unreadCount}</span>
                ) : null}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <span className="hidden font-mono text-xs text-faint sm:inline">{user?.username}</span>
            <Button size="sm" variant="ghost" onClick={() => void logout()}>
              Sign out
            </Button>
          </div>
        </header>
      </div>

      <main id="main" className="mx-auto max-w-6xl px-4 pt-6 pb-28 sm:pb-14">
        <Outlet />
      </main>

      <Onboarding state={onboarding} />

      {/*
        Bottom tabs on mobile: the primary navigation sits in the thumb arc, and
        each target is a full 56px tall. The active tab is marked by a signal
        rule along its top edge rather than a fill, echoing the amber lead-in
        used on the board's column rules.
      */}
      <nav
        aria-label="Main"
        className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur sm:hidden"
      >
        <ul className="flex">
          {items.map((item) => (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative flex min-h-14 flex-col items-center justify-center gap-1 transition ${
                    isActive ? 'text-accent' : 'text-muted'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive ? (
                      <span
                        aria-hidden
                        className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-accent"
                      />
                    ) : null}
                    <span className="relative">
                      {item.icon}
                      {item.to === '/' && unreadCount > 0 ? (
                        <span className="absolute -top-0.5 -right-1.5 size-2 rounded-full bg-accent ring-2 ring-surface" />
                      ) : null}
                    </span>
                    <span className="signage text-[9px]">{item.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
